import yaml from 'js-yaml';
import { useCallback, useMemo, useState } from 'react';
import { ControllerFont } from '@bymason-private/controller-v1-theme-provider';
import {
  Box,
  ClickAwayListener,
  ControllerTextField,
  IconButton,
  MdCopyAll,
  MdDownload,
  Tooltip,
} from '@bymason-private/legacy-pattern-components';
import { ConfigModel } from '@bymason/controller-api-hooks';
import { downloadFile } from '@bymason/utils';

export function YamlConfigDisplay({
  configJson,
  rows,
  minRows = 10,
  maxRows = 25,
  title = 'Mason OS Config',
}: YamlDisplayProps) {
  const [showCopied, setShowCopied] = useState(false);
  const yamlConfig = useMemo(() => (configJson ? yaml.dump(configJson, { lineWidth: -1 }) : undefined), [
    configJson,
  ]);

  const handleDownload = useCallback(() => {
    yamlConfig &&
      downloadFile({
        source: yamlConfig,
        fileName: `${configJson?.os.name || 'configuration'}.yml`,
        type: 'text',
      });
  }, [yamlConfig, configJson?.os.name]);

  const handleCopyToClipboard = useCallback(() => {
    yamlConfig && navigator.clipboard.writeText(yamlConfig);
    setShowCopied(true);
  }, [yamlConfig]);

  const handleTooltipClose = () => {
    setShowCopied(false);
  };

  const additionalTooltipProps = useMemo(
    () =>
      showCopied
        ? {
            title: 'Copied!',
            onClose: handleTooltipClose,
            open: showCopied,
          }
        : {},
    [showCopied]
  );

  return (
    <ControllerTextField
      title={title}
      fullWidth
      aria-readonly
      value={yamlConfig}
      multiline
      rows={rows}
      minRows={minRows}
      maxRows={maxRows}
      readOnly
      InputProps={{
        endAdornment: (
          <Box
            sx={{
              display: 'flex',
              position: 'absolute',
              top: 0.5,
              right: 0.5,
            }}
          >
            <ClickAwayListener onClickAway={handleTooltipClose}>
              <Tooltip title='Copy to clipboard' placement='top' {...additionalTooltipProps}>
                <IconButton onClick={handleCopyToClipboard}>
                  <MdCopyAll />
                </IconButton>
              </Tooltip>
            </ClickAwayListener>
            <Tooltip title='Download' placement='top' {...additionalTooltipProps}>
              <IconButton onClick={handleDownload}>
                <MdDownload />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      }}
      sx={{
        margin: 0,
        marginTop: '12px',
        marginBottom: '12px',
        '& fieldset': {
          borderWidth: 0,
        },
        '&& textarea': {
          ...ControllerFont.Decor300R,
          backgroundColor: (theme) => theme.palette.grey['50'],
          resize: 'vertical',
        },
      }}
    />
  );
}

interface YamlDisplayProps {
  configJson?: ConfigModel;
  title?: string;
  rows?: number;
  minRows?: number;
  maxRows?: number;
}
